import * as React           from "react";

import {
    faBan,
    faBrush,
    faCaretDown,
    faCaretUp,
    faPencil,
    faShoppingCart,
    faSquareCheck,
    IconDefinition,
}                           from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {cx}                 from "emotion";
import ReactTooltip         from "react-tooltip";
import {AnyAction}          from "@Common/react/any-action";
import {cs}                 from "@Common/react/chain-services";
import {
    ComponentLink,
    makeChain,
    valueHelper as value,
}                           from "@Common/react/cst2";
import {Types}              from "@Common/types";
import {buildUrlQuery}      from "@Common/utils/build-url-query";
import {PROGRAM_TYPE}       from "@Xpresso/routes/common/logic/program";
import {getAbsLink}         from "@Xpresso/routes/common/get-abs-link";

type Item = Types.Apis.Payloads.Next.Item.GetPending.Item;

export interface ItemAction {
    icon: IconDefinition,
    label: string,
    name: string,
    run: () => Promise<void>,
}

const PRODUCT_TYPE_PICKPACK             = 6;
const PRODUCT_PROOFENGINE_CUSTCANVAS    = 0;
const PRODUCT_PROOFENGINE_XPRESSPROOF   = 7;
const ORDER_SOURCE_OIA                  = "OIA";

const DEFAULT_ACTION_TEMPLATE = {
    name  : "default",
    label : "Place/Edit",
    icon  : faPencil,
    run   : async () => {},
}

export const ScheduledCampaignsTooltip = () => (
    <em>Changes will only apply to the current edition,
    and will not be applied to your Scheduled Campaign
    default preferences</em>
);

export const makeItemAsActionsMap = ({apis, session, sessionCart, company, routerHistory}:{
    apis          : Types.Xpresso.Loaders.ContextNameMap['apis'],
    session       : Types.Xpresso.Loaders.ContextNameMap['session'],
    sessionCart   : Types.Xpresso.Loaders.ContextNameMap['cart']['value'],
    company       : Types.Xpresso.Loaders.ContextNameMap['company']
    routerHistory : Types.Xpresso.Loaders.ContextNameMap['routerHistory']
}) => (
    ({item}:{item: Item}) => {
        const actions:Array<ItemAction> = [];
        if (!item.legacyOrder && item?.idCart) {
            actions.push({
                name  : "checkout",
                label : "Go to Cart",
                icon  : faShoppingCart,
                run   : () => checkoutOrder({session, order: item, routerHistory}),
            });
        }

        if (sessionCart?.status === "processing" && item.idCart === sessionCart.cartId) {
            actions.push({
                ...DEFAULT_ACTION_TEMPLATE,
                run: async () => window.alert("This cart is processing checkout. Please wait until this process is finished."),
            });
        } else if (item.programType === PROGRAM_TYPE.APM2) {
            actions.push({
                ...DEFAULT_ACTION_TEMPLATE,
                run: () => new Promise(async (resolve) => {
                    await apis.next.session.emulateAgent({branchId: item.idBranch, agentId: item.idAgent});
                    window.location = getAbsLink(`apm?event_id=${item.idEvent}`);

                    // delay link restore since should redirect sooner anyway
                    window.setTimeout(() => resolve(), 1000);
                })
            });
        } else if (item.legacyOrder || item.idProductType === PRODUCT_TYPE_PICKPACK) {
            actions.push({
                ...DEFAULT_ACTION_TEMPLATE,
                run: async () => routerHistory.push(`/product-options/order/${item.orderId}`),
            });
        } else {
            actions.push({
                name  : "product-options",
                label : "Product Options",
                icon  : faSquareCheck,
                run   : async () => routerHistory.push(`/product-options/order/${item.orderId}`),
            });

            const isCC     = item.proofEngineType === PRODUCT_PROOFENGINE_CUSTCANVAS;
            const isXProof = item.proofEngineType === PRODUCT_PROOFENGINE_XPRESSPROOF;
            if (company?.prefs?.xpressoKitOptionsActive && item.isKit) {
                actions.push({
                    name  : "kit-options",
                    label : "Kit Options",
                    icon  : faBrush,
                    run   : async () => routerHistory.push(`/kit-options/order/${item.orderId}`),
                });
            }
            else if (
                (!company?.prefs?.xpressoKitOptionsActive || !item.isKit)
                && item.source !== ORDER_SOURCE_OIA
                && (isCC || isXProof)
                && !item.uyoMPB // no customization for brochures that use multipage validation
            ) {
                actions.push({
                    name  : "customize",
                    label : "Customize",
                    icon  : faBrush,
                    run   : async () => {
                        const fullItem = await apis.next.item.getItem(item.orderId);
                        const templateOptions = await apis.next.getTemplateOptions(fullItem.sku);

                        // if uses multipage validation, forward to product options - not preventing the option
                        // altogether because of the performance hit to look it up, and this case only applies to
                        // legacy orders using products that were converted to use multipage validation
                        if (templateOptions.enableMultipageValidation)
                            return routerHistory.push(`/product-options/order/${item.orderId}`);

                        const buildPath = templateOptions.build_path;
                        if (isCC || (company.prefs.allowBuild3 && !buildPath) ) {
                            routerHistory.push(`/customize/order/${item.orderId}`);
                        } else {
                            window.location = mapOrderAsLegacyEditUrl(item, {'redirect': "customize"});
                        }
                    },
                });
            }
        }

        return actions;
    }
);

const mapOrderAsLegacyEditUrl = (order:Item, params?:any) =>
{
    const query = {
        'action'       : "edit",
        'order_exists' : "exists",
        'is_api_order' : "legacy",
        'id_order'     : order.orderId,
        'order_type'   : order.product,
        'is_cc_order'  : order.proofEngineType === PRODUCT_PROOFENGINE_CUSTCANVAS ? "cc_order" : "xpressproof",
        'procform'     : 1,
        ...params,
    };
    return getAbsLink(`next/start.php${buildUrlQuery(query)}`);
};
